/**
 * Phase 3.45 — Admin Wallet Config (versioned) + Payout/Shares queues.
 *
 * Path: /admin/wallet
 */
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Wallet, Save, Banknote, Building2, Loader2, Play, Shield, Check, X } from "lucide-react";
import { toast } from "sonner";

const FIELD_DEFS = [
    ["interest_rate_yearly_pct", "Jahreszins (%)", "number", "0.1"],
    ["interest_min_savings_eur", "Mindest-Saldo für Zinsen (€)", "number", "1"],
    ["savings_min_lock_days", "Sparen Lock-Up (Tage)", "number", "1"],
    ["payout_min_eur", "Auszahlung Mindestbetrag (€)", "number", "1"],
    ["payout_fee_eur", "Auszahlungsgebühr (€)", "number", "0.1"],
    ["payout_processing_days", "Auszahlungs-Bearbeitung (Tage)", "number", "1"],
    ["kaution_required_pct", "Kaution empfohlen (%)", "number", "0.5"],
    ["kaution_release_days", "Kaution-Freigabe (Tage)", "number", "1"],
    ["shares_token_eur_per_token", "€ pro Anteils-Token", "number", "0.01"],
    ["shares_equity_threshold_tokens", "Equity-Schwellenwert (Tokens)", "number", "1"],
    ["shares_max_tokens_per_user", "Max Tokens pro User", "number", "1"],
    ["donation_min_eur", "Mindest-Spende (€)", "number", "0.5"],
    ["verrechnung_priority_label", "Verrechnungs-Priorität (Label)", "text", null],
];

function eur(n) { return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(n || 0); }

export default function AdminWalletPage() {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [configs, setConfigs] = useState([]);
    const [active, setActive] = useState(null);
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [payouts, setPayouts] = useState([]);
    const [shares, setShares] = useState([]);
    const [tab, setTab] = useState("config");
    const [runningInterest, setRunningInterest] = useState(false);

    const loadAll = useCallback(async () => {
        try {
            const [c, p, s] = await Promise.all([
                api.get("/admin/wallet/config"),
                api.get("/admin/wallet/payouts"),
                api.get("/admin/wallet/shares-orders"),
            ]);
            setConfigs(c.data.items || []);
            setActive(c.data.active);
            setForm(c.data.active);
            setPayouts(p.data.items || []);
            setShares(s.data.items || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Konnte Wallet-Daten nicht laden");
        }
    }, []);

    useEffect(() => {
        if (!authLoading && user && user.role !== "admin") navigate("/");
        if (user?.role === "admin") loadAll();
    }, [user, authLoading, navigate, loadAll]);

    const saveConfig = async () => {
        if (!form) return;
        setSaving(true);
        try {
            const payload = { ...form };
            delete payload.version; delete payload.effective_from; delete payload.is_default;
            delete payload.created_at; delete payload.created_by; delete payload.id;
            const r = await api.post("/admin/wallet/config", payload);
            toast.success(`Version ${r.data.version} gespeichert`);
            loadAll();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally { setSaving(false); }
    };

    const decidePayout = async (tx, status) => {
        const ref = status === "paid" ? prompt("SEPA-Referenz / Verwendungszweck (optional):") || "" : "";
        const note = prompt(status === "paid" ? "Admin-Notiz:" : "Grund für Ablehnung:") || "";
        try {
            await api.patch(`/admin/wallet/payouts/${tx.id}`, { status, admin_note: note, reference: ref });
            toast.success(`Auszahlung ${status === "paid" ? "freigegeben" : "abgelehnt"}`);
            loadAll();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Fehler");
        }
    };

    const decideShares = async (tx, status) => {
        const note = prompt("Admin-Notiz:") || "";
        try {
            await api.patch(`/admin/wallet/shares-orders/${tx.id}`, { status, admin_note: note });
            toast.success("Auftrag aktualisiert");
            loadAll();
        } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    };

    const runInterest = async () => {
        setRunningInterest(true);
        try {
            const r = await api.post("/admin/wallet/run-interest");
            toast.success(`${r.data.user_count} User, ${eur(r.data.accrued_total_eur)} verteilt (${r.data.period})`);
        } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
        finally { setRunningInterest(false); }
    };

    if (!user || user.role !== "admin") return null;
    if (!form) return <div className="p-8 flex items-center gap-3 text-gray-500"><Loader2 className="w-5 h-5 animate-spin" />Lade…</div>;

    const pendingPayouts = payouts.filter((p) => p.status === "pending");
    const pendingShares = shares.filter((s) => s.status === "fulfilled" && s.equity_eligible);

    return (
        <div className="min-h-screen bg-gray-50 pt-20 pb-12" data-testid="admin-wallet-page">
            <div className="max-w-6xl mx-auto px-4 md:px-8">
                <div className="flex items-center gap-3 mb-6">
                    <Wallet className="w-8 h-8 text-[#FFCC00]" />
                    <div>
                        <h1 className="font-display text-3xl font-black">Wallet-Verwaltung</h1>
                        <p className="text-sm text-gray-600 mt-1">Versionierte Wallet-Konfiguration, Auszahlungs- und Anteils-Queue.</p>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex gap-2 border-b border-gray-200 mb-6">
                    {[
                        ["config", "Konfiguration", null],
                        ["payouts", "Auszahlungen", pendingPayouts.length],
                        ["shares", "Anteile", pendingShares.length],
                        ["versions", "Versions-Historie", configs.length],
                    ].map(([k, label, badge]) => (
                        <button
                            key={k}
                            onClick={() => setTab(k)}
                            className={`px-4 py-2 text-sm font-bold uppercase tracking-wider border-b-2 transition-colors ${tab === k ? "border-[#FFCC00] text-[#111827]" : "border-transparent text-gray-500 hover:text-gray-700"}`}
                            data-testid={`admin-wallet-tab-${k}`}
                        >
                            {label} {badge !== null && badge > 0 && <span className="ml-1 bg-[#FFCC00] text-[#111827] text-[10px] px-1.5 py-0.5 rounded-sm">{badge}</span>}
                        </button>
                    ))}
                </div>

                {tab === "config" && (
                    <div className="bg-white border border-gray-200 rounded-sm p-6 space-y-4" data-testid="admin-wallet-config-form">
                        <div className="flex items-center justify-between mb-2 pb-3 border-b border-gray-100">
                            <div>
                                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Aktive Version</div>
                                <div className="font-bold text-base">v{active?.version ?? 0}{active?.is_default && " (Default)"} · gültig ab {active?.effective_from ? new Date(active.effective_from).toLocaleString("de-DE") : "—"}</div>
                            </div>
                            <Button onClick={runInterest} disabled={runningInterest} variant="outline" className="rounded-sm" data-testid="admin-wallet-run-interest">
                                {runningInterest ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />} Zinsen jetzt verbuchen
                            </Button>
                        </div>
                        <p className="text-xs text-gray-600">Beim Speichern wird automatisch eine <strong>neue Version</strong> angelegt. Die alte bleibt erhalten und gilt für bereits abgerechnete Zeiträume.</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {FIELD_DEFS.map(([key, label, type, step]) => (
                                <div key={key}>
                                    <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">{label}</Label>
                                    <Input
                                        type={type}
                                        step={step || undefined}
                                        value={form[key] ?? ""}
                                        onChange={(e) => setForm({ ...form, [key]: type === "number" ? parseFloat(e.target.value) : e.target.value })}
                                        className="mt-1 rounded-sm"
                                        data-testid={`admin-wallet-field-${key}`}
                                    />
                                </div>
                            ))}
                            <div className="md:col-span-2">
                                <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Notizen / Changelog</Label>
                                <Textarea
                                    rows={2}
                                    value={form.notes || ""}
                                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                    className="mt-1 rounded-sm"
                                    data-testid="admin-wallet-field-notes"
                                />
                            </div>
                        </div>
                        <Button onClick={saveConfig} disabled={saving} className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold rounded-sm uppercase tracking-wider w-full" data-testid="admin-wallet-save-btn">
                            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Neue Version speichern
                        </Button>
                    </div>
                )}

                {tab === "payouts" && (
                    <div className="bg-white border border-gray-200 rounded-sm" data-testid="admin-wallet-payouts">
                        {payouts.length === 0 ? (
                            <div className="p-8 text-center text-gray-500 text-sm">Keine Auszahlungsanträge.</div>
                        ) : (
                            <div className="divide-y divide-gray-100">
                                {payouts.map((p) => (
                                    <div key={p.id} className="p-4 flex flex-col md:flex-row items-start md:items-center gap-3" data-testid={`payout-row-${p.id}`}>
                                        <Banknote className="w-5 h-5 text-amber-600 shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <div className="font-bold text-sm">{p.user_label}</div>
                                            <div className="text-xs text-gray-600">{p.user_email} · IBAN: {p.bank_iban || "—"} · {p.bank_account_holder || "—"}</div>
                                            <div className="text-[10px] text-gray-500 mt-1">Beantragt: {new Date(p.created_at).toLocaleString("de-DE")}{p.note ? ` · ${p.note}` : ""}</div>
                                        </div>
                                        <div className="font-mono-num font-black text-lg">{eur(p.amount_eur)}</div>
                                        <div className="flex gap-2">
                                            {p.status === "pending" ? (
                                                <>
                                                    <Button onClick={() => decidePayout(p, "paid")} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm" data-testid={`payout-pay-${p.id}`}><Check className="w-4 h-4 mr-1" />Bezahlt</Button>
                                                    <Button onClick={() => decidePayout(p, "rejected")} size="sm" variant="outline" className="rounded-sm" data-testid={`payout-reject-${p.id}`}><X className="w-4 h-4 mr-1" />Ablehnen</Button>
                                                </>
                                            ) : (
                                                <span className={`px-2 py-1 rounded-sm text-[10px] font-bold uppercase tracking-wider ${p.status === "paid" ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}>{p.status}</span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {tab === "shares" && (
                    <div className="bg-white border border-gray-200 rounded-sm" data-testid="admin-wallet-shares">
                        {shares.length === 0 ? (
                            <div className="p-8 text-center text-gray-500 text-sm">Keine Anteils-Käufe.</div>
                        ) : (
                            <div className="divide-y divide-gray-100">
                                {shares.map((s) => (
                                    <div key={s.id} className="p-4 flex flex-col md:flex-row items-start md:items-center gap-3" data-testid={`shares-row-${s.id}`}>
                                        <Building2 className="w-5 h-5 text-violet-600 shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <div className="font-bold text-sm">User: {s.user_id.slice(0, 8)}…</div>
                                            <div className="text-xs text-gray-600">{s.tokens} Tokens · Status: <strong>{s.status}</strong>{s.equity_eligible && <span className="ml-2 text-violet-700 font-bold">⭐ Equity-berechtigt</span>}</div>
                                            <div className="text-[10px] text-gray-500 mt-1">{new Date(s.created_at).toLocaleString("de-DE")}{s.admin_note ? ` · ${s.admin_note}` : ""}</div>
                                        </div>
                                        <div className="font-mono-num font-black text-lg">{eur(s.amount_eur)}</div>
                                        {s.equity_eligible && s.status !== "converted_to_equity" && (
                                            <Button onClick={() => decideShares(s, "converted_to_equity")} size="sm" className="bg-violet-600 hover:bg-violet-700 text-white rounded-sm" data-testid={`shares-convert-${s.id}`}>
                                                <Shield className="w-4 h-4 mr-1" />Equity ausstellen
                                            </Button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {tab === "versions" && (
                    <div className="bg-white border border-gray-200 rounded-sm" data-testid="admin-wallet-versions">
                        {configs.length === 0 ? (
                            <div className="p-8 text-center text-gray-500 text-sm">Noch keine Versionen — Default ist aktiv.</div>
                        ) : (
                            <div className="divide-y divide-gray-100">
                                {configs.map((c) => (
                                    <div key={c.id || c.version} className="p-4" data-testid={`version-row-${c.version}`}>
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="font-bold">Version {c.version}</div>
                                            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">gültig ab {new Date(c.effective_from).toLocaleString("de-DE")}</div>
                                        </div>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                                            <div><span className="text-gray-500">Zins:</span> {c.interest_rate_yearly_pct}%</div>
                                            <div><span className="text-gray-500">Min-Auszahlung:</span> {eur(c.payout_min_eur)}</div>
                                            <div><span className="text-gray-500">Token €:</span> {eur(c.shares_token_eur_per_token)}</div>
                                            <div><span className="text-gray-500">Equity-Schwelle:</span> {c.shares_equity_threshold_tokens}</div>
                                        </div>
                                        {c.notes && <div className="mt-2 text-xs text-gray-600 italic">„{c.notes}"</div>}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
